import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { actions } from './redux/actions';
import Strings from '../../../Strings';
import { DATE_MONTH_DAY_YEAR, HEADER_BUTTON_WHITE, TIMEZONES } from '../../../constants';
import './UserDetail.scss';
import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { DatePickerField, Input, MultiSelectField, SelectField } from '../../../containers/Form';
import { SelectedOptions, SelectedOptionsType } from '../../../components/Select/Multiselect';
import { AddBillingOrgModal, AddRoleModal, PasswordResetModal, SetPasswordModal } from './UserModals';
import LoadingRenderer from '../../../components/LoadingRenderer';
import { openModalAction } from '../../../actions/modal';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { isCurrentUserAnonymized, isMe, isOrganizationManager, isSuperUser, SUPERUSER } from '../../../utils/userRoles';
import userActions, { EDIT_USER_ERROR } from '../../../actions/user';
import { isEqual, set } from 'lodash';

export const PENDING_ROLE_ARCHETYPE = "pending_role";
export const allowedExtraRoles = ['capmanager', 'doctor', 'patient']
const EMPTY_ORG_UUID = '00000000-0000-0000-0000-000000000000';

const getErrorMessage = (errorOrAction, fallbackMessage) => {
  if (!errorOrAction) return fallbackMessage;

  if (typeof errorOrAction === 'string') {
    return errorOrAction;
  }

  const data = errorOrAction?.response?.data;

  if (typeof data === 'string' && data) {
    return data;
  }

  return data?.standard_error?.message
    || data?.message
    || data?.error
    || errorOrAction?.message
    || fallbackMessage;
};

const checkPasswordRequirements = (password) => ({
  minLength: password.length >= 8,
  hasUppercase: /[A-Z]/.test(password),
  hasLowercase: /[a-z]/.test(password),
  hasNumber: /[0-9]/.test(password),
  hasSpecial: /[!@#$%^&*()_+\-=[\]{}|;':",./<>?]/.test(password),
});

const isPasswordValid = (reqs) =>
  reqs.minLength && reqs.hasUppercase && reqs.hasLowercase && reqs.hasNumber && reqs.hasSpecial;

const parseDateOnly = (value) => {
  if (!value) {
    return null;
  }

  if (moment.isMoment(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = moment(value.slice(0, 10), 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
  }

  return null;
};

const formatDateOnly = (value) => {
  const parsed = parseDateOnly(value);
  return parsed ? parsed.format('YYYY-MM-DD') : null;
};

function UserDetail(props) {
  const { match, getUserDetails, editUser, onNavigate, getUserFormBundle, sendPasswordReset, setUserPassword } = props;
  const { addUserRole, addUserNativeRole, openConfirmModal, showNotification } = props;
  const userId = match?.params?.userId;
  const showFullInfo = isSuperUser();

  const [user, setUser] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingAccount, setIsEditingAccount] = React.useState(false);
  const [isEditingOrgRole, setIsEditingOrgRole] = React.useState(false);
  const [editedUser, setEditedUser] = React.useState({});
  const [organizations, setOrganizations] = React.useState([]);
  const [archetypes, setArchetypes] = React.useState([]);
  const [filteredRoles, setFilteredRoles] = React.useState([]);
  const [showPasswordResetModal, setShowPasswordResetModal] = React.useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = React.useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = React.useState(false);
  const [setPasswordLoading, setSetPasswordLoading] = React.useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = React.useState(false);
  const [addRoleLoading, setAddRoleLoading] = React.useState(false);
  const [showAddBillingOrg, setShowAddBillingOrg] = React.useState(false);

  const displayUser = (isEditing || isEditingAccount || isEditingOrgRole) ? { ...user, ...editedUser } : user;
  const userOriginalArchetype = user?.roles?.find(r => r.id === user.original_role)?.archetype;

  const requesterAnonymize = isCurrentUserAnonymized();
  const isEditingSelf = Boolean(user?.id && isMe(user.id));
  const canChangeAnonymize = (isSuperUser() || isOrganizationManager()) && !requesterAnonymize && !isEditingSelf;
  const anonymizeDisabledReason = React.useMemo(() => {
    if (canChangeAnonymize) {
      return '';
    }
    if (isEditingSelf) {
      return 'You cannot change anonymize for your own account';
    }
    if (requesterAnonymize) {
      return 'A user with anonymize enabled cannot change anonymize';
    }
    return 'Only superuser or organization manager can change anonymize';
  }, [canChangeAnonymize, isEditingSelf, requesterAnonymize]);
  const anonymizeLabel = anonymizeDisabledReason ? (
    <span data-tooltip-id="tooltip" data-tooltip-content={anonymizeDisabledReason}>
      {Strings.maskPHIData || 'Mask PHI Data'}
    </span>
  ) : (Strings.maskPHIData || 'Mask PHI Data');

  React.useEffect(() => {
    if (userId && getUserDetails) {
      setIsLoading(true);
      setError(null);
      getUserDetails(userId)
        .then(resp => {
          const userData = resp?.response;
          if (userData) {
            setUser(userData);
          } else {
            setError('User not found');
          }
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to load user:', err);
          setError('Failed to load user');
          setIsLoading(false);
        });
    }
  }, [userId, getUserDetails]);

  React.useEffect(() => {
    if (!user) return;
    // Add missing role uuid
    const original_roleUuid = user?.roles?.find(r => r.id === user.original_role)?.uuid || null;
    if (original_roleUuid !== user.original_roleUuid) {
      setUser(prev => ({ ...prev, original_roleUuid }));
    }
  }, [user?.roles, user?.original_role]);


  // Load form bundle (organizations with nested roles, and archetypes)
  React.useEffect(() => {
    if (getUserFormBundle) {
      getUserFormBundle().then(resp => {
        if (resp?.response) {
          const bundle = resp.response;
          if (bundle.organizations) {
            setOrganizations(bundle.organizations?.map(org => ({ ...org, value: org.id, label: org.name })));
          }
          if (bundle.archetypes) {
            setArchetypes(bundle.archetypes);
          }
        }
      });
    }
  }, [getUserFormBundle]);

  // Filter roles based on selected organization (roles are nested under organizations)
  React.useEffect(() => {
    // Check if org_id has been edited, otherwise use user's org_id
    const selectedOrgId = 'org_id' in editedUser ? editedUser.org_id : user?.org_id;

    // Check if orgless (null, undefined, or empty UUID)
    const isOrgless = !selectedOrgId || selectedOrgId === '00000000-0000-0000-0000-000000000000';

    if (organizations.length > 0) {
      if (isOrgless) {
        // Orgless - find the "orgless" entry
        const orglessOrg = organizations.find(org => org.id === 'orgless');
        if (orglessOrg && orglessOrg.roles) {
          setFilteredRoles(orglessOrg.roles.map(r => ({ ...r, value: r.id, label: r.title })));
        } else {
          setFilteredRoles([]);
        }
      } else {
        // Find the selected organization and get its roles
        const selectedOrg = organizations.find(org => String(org.id) === String(selectedOrgId));
        if (selectedOrg && selectedOrg.roles) {
          setFilteredRoles(selectedOrg.roles.map(r => ({ ...r, value: r.id, label: r.title })));
        } else {
          setFilteredRoles([]);
        }
      }
    } else {
      setFilteredRoles([]);
    }
  }, [editedUser.org_id, editedUser, user?.org_id, organizations]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldName = name;

    if (fieldName === 'anonymize' && !canChangeAnonymize) {
      return;
    }

    if (!fieldName) {
      return;
    }

    const newValue = type === 'checkbox' ? checked : value;
    const valuesAreEqual = fieldName === 'date_of_birth'
      ? formatDateOnly(user[fieldName]) === formatDateOnly(newValue)
      : user[fieldName] === newValue;

    // Only track the field if it's different from the original
    if (!valuesAreEqual) {
      setEditedUser(prev => ({
        ...prev,
        [fieldName]: newValue,
      }));
    } else {
      // Remove from editedUser if value is back to original
      setEditedUser(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  const handleSave = async () => {
    if (!editUser || !user) return;

    // Only send if there are actual changes
    if (Object.keys(editedUser).length === 0) {
      setIsEditing(false);
      setIsEditingAccount(false);
      setIsEditingOrgRole(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const requestPayload = { ...editedUser, id: user?.unguessable_id };
    if (requestPayload.anonymize !== undefined && !canChangeAnonymize) {
      delete requestPayload.anonymize;
    }

    try {
      if (Object.prototype.hasOwnProperty.call(requestPayload, 'date_of_birth')) {
        requestPayload.date_of_birth = formatDateOnly(requestPayload.date_of_birth);
      }

      const editResp = await editUser(requestPayload);
      if (editResp?.type === EDIT_USER_ERROR) {
        throw new Error(editResp?.response?.data?.standard_error?.message || 'Failed to edit user');
      }
      // Reload user data
      const resp = await getUserDetails(userId);
      if (resp?.response) {
        setUser(resp.response);
      }
      showNotification('User updated successfully');
      setIsEditing(false);
      setIsEditingAccount(false);
      setIsEditingOrgRole(false);
      setEditedUser({});
      setIsLoading(false);
    } catch (err) {
      showNotification(err.message || 'Failed to update user', 5000, true);
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('/admin/users');
    }
  };

  const handleSendPasswordReset = async (method) => {
    if (!sendPasswordReset || !user) return;

    setPasswordResetLoading(true);
    try {
      const resetResp = await sendPasswordReset(userId, method);
      if (resetResp?.type === 'SEND_PASSWORD_RESET/error') {
        throw new Error(resetResp?.response?.data?.standard_error?.message || 'Failed to send password reset');
      }
      showNotification(`Password reset ${method === 'email' ? 'email' : 'SMS'} sent successfully`);
      setShowPasswordResetModal(false);
    } catch (err) {
      console.error('Failed to send password reset:', err);
      showNotification(err.message || `Failed to send password reset ${method === 'email' ? 'email' : 'SMS'}`, 5000, true);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleSetPassword = async newPassword => {
    if (!setUserPassword || !user) return;

    const reqs = checkPasswordRequirements(newPassword);
    if (!isPasswordValid(reqs)) {
      return;
    }

    setSetPasswordLoading(true);
    try {
      const setPasswordResp = await setUserPassword(userId, newPassword);
      if (setPasswordResp?.type === 'SET_USER_PASSWORD/error') {
        throw new Error(setPasswordResp?.response?.data?.standard_error?.message || 'Failed to set password');
      }
      showNotification('Password set successfully');
      setShowSetPasswordModal(false);
    } catch (err) {
      console.error('Failed to set password:', err);
      showNotification(err.message || 'Failed to set password', 5000, true);
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = moment(dateString);
    return date.format(DATE_MONTH_DAY_YEAR);
  };

  const handleAddBillingOrg = async (orgIds) => {
    if (!editUser || !user) return;

    // Only send if there are actual changes
    if (isEqual(orgIds, user.billing_org_ids)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const requestPayload = { billing_org_ids: orgIds, id: user?.unguessable_id };

    try {
      const editResp = await editUser(requestPayload);
      if (editResp?.type === EDIT_USER_ERROR) {
        throw new Error(editResp?.response?.data?.standard_error?.message || 'Failed to edit user');
      }
      // Reload user data
      const resp = await getUserDetails(userId);
      if (resp?.response) {
        setUser(resp.response);
      }
      showNotification('User updated successfully');
      setIsLoading(false);
      setShowAddBillingOrg(false);
    } catch (err) {
      showNotification(err.message || 'Failed to update user', 5000, true);
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
      setIsLoading(false);
    }
  };
  
  const handleAddRole = async (addRoleRoleId, addRoleOrgId) => {
    if (!addUserRole || !addRoleRoleId || !user?.unguessable_id) return;

    const normalizedUserOrg = !user?.org_id || user?.org_id === EMPTY_ORG_UUID ? 'orgless' : String(user.org_id);
    const normalizedSelectedOrg = !addRoleOrgId || addRoleOrgId === EMPTY_ORG_UUID ? 'orgless' : String(addRoleOrgId);

    if (userOriginalArchetype === 'patient' && normalizedUserOrg !== normalizedSelectedOrg) {
      showNotification(Strings.roleSwitcher?.patientRoleCannotBeExtended || 'Patient role cannot be extended to another organization.', 5000, true);
      return;
    }

    setAddRoleLoading(true);
    try {
      const addRoleResp = await addUserRole(user.unguessable_id, addRoleRoleId);
      if (addRoleResp?.type?.endsWith('/error')) {
        throw new Error(getErrorMessage(addRoleResp, 'Failed to add role'));
      }
      // Reload user data to get updated roles
      const resp = await getUserDetails(userId);
      if (resp?.response) {
        setUser(resp.response);
      }
      setShowAddRoleModal(false);
    } catch (err) {
      showNotification(getErrorMessage(err, 'Failed to add role'), 5000, true);
      console.error('Failed to add role:', err);
    }
    setAddRoleLoading(false);
  };

  const handleChangeNativeRole = async () => {
    if (!addUserNativeRole || !editedUser.original_role || !user?.unguessable_id) return;

    setAddRoleLoading(true);
    try {
      const addNativeRoleResp = await addUserNativeRole(user.unguessable_id, editedUser.original_role);
      if (addNativeRoleResp?.type === 'ADD_USER_NATIVE_ROLE/error') {
        throw new Error(addNativeRoleResp?.response?.data?.standard_error?.message || 'Failed to add native role');
      }
      // Reload user data to get updated roles
      const resp = await getUserDetails(userId);
      if (resp?.response) {
        setUser(resp.response);
      }
      setIsEditingOrgRole(false);
    } catch (err) {
      showNotification(err.message || 'Failed to add native role', 5000, true);
      console.error('Failed to add native role:', err);
    }
    setAddRoleLoading(false);
  };

  const handleRemoveRole = async (roleUuid) => {
    if (!user?.unguessable_id) return;

    const roleTitle = user.roles?.find(r => r.uuid === roleUuid)?.title || 'this role';
    const data = {
      title: Strings.formatString(Strings.removeRoleWarning, roleTitle, `${user.first_name} ${user.last_name}`),
      onConfirmAction: actions.removeUserRole(user.unguessable_id, roleUuid),
      confirmPostAction: async () => {
        const resp = await getUserDetails(userId);
        if (resp?.response) {
          setUser(resp.response);
        }
      },
      caption: Strings.removeRole,
      hideCaution: true,
    };
    openConfirmModal(data);
  };

  if (error && !user) {
    return (
      <div className="user-detail-page">
        <div className="error-message">
          <p>{error}</p>
        </div>
        <button className="btn-back" onClick={handleBack}>
          {Strings.backToUsers || 'Back to Users'}
        </button>
      </div>
    );
  }

  const isUserSuperuser = user?.roles?.some(r => r.archetype === SUPERUSER);
  const isUserShipper = user?.roles?.some(r => r.archetype === 'shipper');

  return (
    <LoadingRenderer isLoading={isLoading}>
      <PageHeader
        onBackClick={() => history.back()}
        left={`${user.first_name} ${user.last_name}`}
        right={(
          <>
            <Button onClick={() => setShowPasswordResetModal(true)} class={HEADER_BUTTON_WHITE} title={Strings.sendPasswordReset || 'Send Password Reset'} />
            <Button onClick={() => setShowSetPasswordModal(true)} class={HEADER_BUTTON_WHITE} title={Strings.setPassword || 'Set Password'} />
          </>
        )}
      />

      {error && (
        <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
          <p>{error}</p>
        </div>
      )}
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'leave', autoHideDelay: '100' } }}
        className="scrollbar-right-margin"
      >
        <div className="rjsf">
          <fieldset>
            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.identifiers || 'Identifiers'}</header>
                <Input type="text" id="unguessable_id" label={Strings.unguessableId || 'Unguessable ID'} value={user.unguessable_id || user.uuid || 'N/A'} readOnly />
                <Input type="text" id="uuid" label={Strings.uuid || 'UUID'} value={user.unguessable_id || user.uuid || 'N/A'} readOnly />
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header className="header-with-button">
                  {Strings.personalInformation || 'Personal Information'}
                  {isEditing ? (
                    <div className="buttons">
                      <Button
                        class={HEADER_BUTTON_WHITE}
                        onClick={() => {
                          setIsEditing(false);
                          setEditedUser({});
                        }}
                        disabled={isLoading}
                        title={Strings.cancel || 'Cancel'}
                      />
                      <Button class={HEADER_BUTTON_DARK_BLUE} onClick={handleSave} disabled={isLoading} title={isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')} />
                    </div>
                  ) : (
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={() => {
                        setIsEditing(true);
                        setEditedUser({});
                      }}
                      title={Strings.editPersonalInformation || 'Edit Personal Information'}
                    />
                  )}
                </header>
                <Input
                  id="first_name"
                  name="first_name"
                  type="text"
                  label={Strings.firstName || 'First Name'}
                  value={displayUser.first_name || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input
                  id="last_name"
                  name="last_name"
                  type="text"
                  label={Strings.lastName || 'Last Name'}
                  value={displayUser.last_name || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label={Strings.email || 'Email'}
                  value={displayUser.email || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input
                  id="username"
                  name="username"
                  type="text"
                  label={Strings.username || 'Username'}
                  value={displayUser.username || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  label={Strings.phone || 'Phone'}
                  value={displayUser.phone || ''}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />

                <SelectField
                  id="timezone"
                  name="timezone"
                  label={Strings.timezone || 'Timezone'}
                  value={displayUser.timezone || ''}
                  data={TIMEZONES.map(tz => ({ value: tz.timezone, label: tz.text }))}
                  onChange={option => handleInputChange({ target: { name: 'timezone', value: option ? option.value : '' } })}
                  readOnly={!isEditing}
                />
                <DatePickerField
                  id="date_of_birth"
                  label={Strings.dateOfBirth || 'Date of Birth'}
                  name="date_of_birth"
                  value={parseDateOnly(displayUser.date_of_birth)}
                  onChange={value => handleInputChange({ target: { name: 'date_of_birth', value } })}
                  disableFuture
                  actions={['cancel', 'accept']}
                  readOnly={!isEditing}
                />
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header className="header-with-button">
                  {Strings.roleAndOrganization || 'Role & Organization'}
                  {isEditingOrgRole ? (
                    <div className="buttons">
                      <Button
                        class={HEADER_BUTTON_WHITE}
                        onClick={() => {
                          setIsEditingOrgRole(false);
                          setEditedUser({});
                        }}
                        disabled={isLoading}
                        title={Strings.cancel || 'Cancel'}
                      />
                      <Button class={HEADER_BUTTON_DARK_BLUE} onClick={handleChangeNativeRole} disabled={isLoading} title={isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')} />
                    </div>
                  ) : (
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={() => {
                        setIsEditingOrgRole(true);
                        setEditedUser({});
                      }}
                      title={Strings.editRoleAndOrganization || 'Edit Original Role & Organization'}
                    />
                  )}
                </header>
                <SelectField
                  id="org_id"
                  value={!displayUser.org_id || displayUser.org_id === '00000000-0000-0000-0000-000000000000' ? 'orgless' : displayUser.org_id}
                  onChange={option => {
                    // Clear original_role_id when org changes, set null for orgless
                    const newOrgId = option.value === 'orgless' ? null : (option.value || undefined);
                    setEditedUser(prev => {
                      const updated = { ...prev, org_id: newOrgId };
                      delete updated.original_role;
                      return updated;
                    });
                  }}
                  data={organizations.filter(org => org.id !== 'orgless').concat(isSuperUser() ? [{ value: 'orgless', label: Strings.orgless || 'Orgless' }] : [])}
                  label={Strings.organization || 'Organization'}
                  readOnly={!isEditingOrgRole}
                />
                <SelectField
                  id="original_role_id"
                  value={displayUser.original_roleUuid || ''}
                  data={filteredRoles
                    .filter(role => role.archetype !== PENDING_ROLE_ARCHETYPE && allowedExtraRoles.includes(role.archetype))
                    .concat(user.roles?.filter(r => r.id === displayUser.original_role && !allowedExtraRoles.includes(r.archetype))
                      .map(r => ({ ...r, value: r.uuid, label: r.title })) || [])}
                  onChange={option => handleInputChange({ target: { name: 'original_role', value: option ? option.value : '' } })}
                  label={Strings.originalRole || 'Original Role'}
                  readOnly={!isEditingOrgRole}
                />
                <div className="form-group field field-object grid-full-width">
                  <fieldset>
                    <header className="header-with-button">
                      {Strings.userRoles || 'User Roles'}
                      <Button
                        class={HEADER_BUTTON_DARK_BLUE}
                        onClick={() => setShowAddRoleModal(true)}
                        title={Strings.addRole || 'Add Role'}
                        disabled={userOriginalArchetype === PENDING_ROLE_ARCHETYPE}
                        tooltip={userOriginalArchetype === PENDING_ROLE_ARCHETYPE 
                          ? 'Change the original user role before adding a new role' 
                          : (Strings.roleSwitcher?.inherentSwitchableRoles || '')}
                      />
                    </header>
                    <SelectedOptions
                      id="user-roles"
                      items={
                        user.roles ? user.roles.map(r => 
                          ({ ...r, value: r.id, label: r.title, disabled: r.id === user.original_role, tooltip:  r.id === user.original_role ? 'Cannot remove original role' : '' }))
                        : []
                      }
                      label={Strings.assignedRoles || 'Assigned Roles'}
                      options={user.roles ? user.roles.map(r => ({ ...r, value: r.id, label: r.title })) : []}
                      className="grid-full-width"
                      type={SelectedOptionsType.cloud}
                      withClearButton={false}
                      onRemove={role => handleRemoveRole(role.uuid)}
                    />
                  </fieldset>
                </div>
              </fieldset>
            </div>

            {showFullInfo && (
              (isUserSuperuser || isUserShipper) && (
                <div className="form-group field field-object leaf">
                  <fieldset>
                    <header className="header-with-button">
                      {Strings.billingOrgs || 'Billing Organizations'}
                      <Button
                        class={HEADER_BUTTON_DARK_BLUE}
                        onClick={() => setShowAddBillingOrg(true)}
                        title={Strings.addBillingOrgs || 'Add Billing Organizations'}
                      />
                    </header>
                    <SelectedOptions
                      id="billing-orgs"
                      items={
                        displayUser.billing_org_ids ? displayUser.billing_org_ids.map(org => 
                          organizations.find(o => o.id === org) || { id: org, value: org, label: org })
                        : []
                      }
                      label={Strings.associatedBillingOrgs || 'Associated Billing Orgs'}
                      options={organizations.map(org => ({ ...org, value: org.id, label: org.name }))}
                      className="grid-full-width"
                      type={SelectedOptionsType.cloud}
                      withClearButton={false}
                      onRemove={removedOrg => handleAddBillingOrg(displayUser.billing_org_ids.filter(orgId => orgId !== removedOrg.id))}
                    />
                  </fieldset>
                </div>
              )
            )}

            <div className="form-group field field-object">
              <fieldset>
                <header className="header-with-button">
                  {Strings.account || 'Account'}
                  {isEditingAccount ? (
                    <div className="buttons">
                      <Button
                        class={HEADER_BUTTON_WHITE}
                        onClick={() => {
                          setIsEditingAccount(false);
                          setEditedUser({});
                        }}
                        disabled={isLoading}
                        title={Strings.cancel || 'Cancel'}
                      />
                      <Button class={HEADER_BUTTON_DARK_BLUE} onClick={handleSave} disabled={isLoading} title={isLoading ? (Strings.saving || 'Saving...') : (Strings.saveChanges || 'Save Changes')} />
                    </div>
                  ) : (
                    <Button
                      class={HEADER_BUTTON_DARK_BLUE}
                      onClick={() => {
                        setIsEditingAccount(true);
                        setEditedUser({});
                      }}
                      title={Strings.editAccountSettingsAndStatus || 'Edit Account Settings & Status'}
                    />
                  )}
                </header>
                <div className="form-group field field-object leaf">
                  <fieldset>
                    <header>{Strings.accountSettings || 'Account Settings'}</header>
                    {showFullInfo && (
                      <Input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={displayUser.is_active}
                        label={Strings.activeAccount || 'Active Account'}
                        onChange={handleInputChange}
                        readOnly={!isEditingAccount}
                        disabled={!isEditingAccount}
                        rowClassName="checkbox-row"
                      />
                    )}
                    {showFullInfo && (
                      <Input
                        type="checkbox"
                        id="read_only"
                        name="read_only"
                        checked={displayUser.read_only}
                        label={Strings.writePrivileges || 'Write Privileges'}
                        onChange={handleInputChange}
                        readOnly={!isEditingAccount}
                        disabled={!isEditingAccount}
                        rowClassName="checkbox-row"
                      />
                    )}
                    <Input
                      type="checkbox"
                      id="timer_off"
                      name="timer_off"
                      checked={!displayUser.timer_off}
                      label={Strings.adminTimerRights}
                      onChange={e => handleInputChange({ ...e, target: { ...e.target, name: 'timer_off', value: !e.target.checked } })}
                      readOnly={!isEditingAccount}
                      disabled={!isEditingAccount}
                      rowClassName="checkbox-row"
                    />
                    <Input
                      type="checkbox"
                      id="anonymize"
                      name="anonymize"
                      checked={displayUser.anonymize}
                      label={anonymizeLabel}
                      onChange={handleInputChange}
                      readOnly={!isEditingAccount}
                      disabled={!isEditingAccount || !canChangeAnonymize}
                      rowClassName={`checkbox-row ${(!isEditingAccount || !canChangeAnonymize) ? 'checkbox-row-disabled' : ''}`}
                    />
                  </fieldset>
                </div>
                {showFullInfo && (
                  <div className="form-group field field-object leaf">
                    <fieldset>
                      <header>{Strings.accountStatusFlags || 'Account Status Flags'}</header>
                      <Input
                        type="checkbox"
                        name="is_superuser"
                        checked={displayUser.is_superuser || false}
                        onChange={handleInputChange}
                        readOnly={!isEditing}
                        label={Strings.isSuperuser || 'Is Superuser'}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="is_staff"
                        checked={displayUser.is_staff || false}
                        onChange={handleInputChange}
                        readOnly={!isEditing}
                        label={Strings.isStaff || 'Is Staff'}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="is_verified"
                        checked={displayUser.is_verified || false}
                        onChange={handleInputChange}
                        label={Strings.isVerified || 'Verified'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="email_verified"
                        checked={displayUser.email_verified || false}
                        onChange={handleInputChange}
                        label={Strings.emailVerified || 'Email Verified'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="mobile_verified"
                        checked={displayUser.mobile_verified || false}
                        onChange={handleInputChange}
                        label={Strings.mobileVerified || 'Mobile Verified'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="is_paused"
                        checked={displayUser.is_paused || false}
                        onChange={handleInputChange}
                        label={Strings.isPaused || 'Paused'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="is_deleted"
                        checked={displayUser.is_deleted || false}
                        onChange={handleInputChange}
                        label={Strings.isDeleted || 'Deleted'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="is_test_account"
                        checked={displayUser.is_test_account || false}
                        onChange={handleInputChange}
                        label={Strings.isTestAccount || 'Test Account'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                      <Input
                        type="checkbox"
                        name="whitelisted"
                        checked={displayUser.whitelisted || false}
                        onChange={handleInputChange}
                        label={Strings.whitelisted || 'Whitelisted'}
                        readOnly={!isEditing}
                        rowClassName="checkbox-row"
                      />
                    </fieldset>
                  </div>
                )}
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.externalIdsAndEula || 'External IDs & EULA'}</header>
                <Input
                  type="text"
                  name="external_user_id"
                  value={displayUser.external_user_id || ''}
                  label={Strings.externalUserId || 'External User ID'}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input
                  type="text"
                  name="pharmacy_patient_id"
                  value={displayUser.pharmacy_patient_id || ''}
                  label={Strings.pharmacyPatientId || 'Pharmacy Patient ID'}
                  onChange={handleInputChange}
                  readOnly={!isEditing}
                />
                <Input type="text" id="eula_accepted_version" label={Strings.eulaAcceptedVersion || 'EULA Version'} value={user.eula_accepted_version || 'N/A'} readOnly />
                <Input type="text" id="eula_accepted_at" label={Strings.eulaAcceptedAt || 'EULA Accepted At'} value={formatDate(user.eula_accepted_at)} readOnly />
              </fieldset>
            </div>

            <div className="form-group field field-object leaf">
              <fieldset>
                <header>{Strings.activity || 'Activity'}</header>
                <Input type="text" id="created_at" label={Strings.created || 'Created'} value={formatDate(user.created_at)} readOnly />
                <Input type="text" id="updated_at" label={Strings.updatedAt || 'Updated At'} value={formatDate(user.updated_at)} readOnly />
                <Input type="text" id="last_login" label={Strings.lastLogin || 'Last Login'} value={formatDate(user.last_login)} readOnly />
                <Input type="text" id="portal_last_login_at" label={Strings.portalLastLogin || 'Portal Last Login'} value={formatDate(user.portal_last_login_at)} readOnly />
                {showFullInfo && <Input type="text" id="app_last_login_at" label={Strings.appLastLogin || 'App Last Login'} value={formatDate(user.app_last_login_at)} readOnly />}
              </fieldset>
            </div>
          </fieldset>
        </div>
      </OverlayScrollbarsComponent>

      {/* Password Reset Modal */}
      {showPasswordResetModal && (
        <PasswordResetModal
          user={user}
          onClose={() => setShowPasswordResetModal(false)}
          onSubmit={handleSendPasswordReset}
          loading={passwordResetLoading}
        />
      )}

      {/* Set Password Modal */}
      {showSetPasswordModal &&  (
        <SetPasswordModal
          user={user}
          onClose={() => setShowSetPasswordModal(false)}
          onSubmit={handleSetPassword}
          loading={setPasswordLoading}
        />
      )}

      {showAddRoleModal && (
        <AddRoleModal
          organizations={organizations}
          userRoles={user.roles}
          isPatientUser={userOriginalArchetype === 'patient'}
          userOrgId={user?.org_id}
          onClose={() => {setShowAddRoleModal(false)}}
          onSubmit={handleAddRole}
          loading={addRoleLoading}
        />
      )}

      {showAddBillingOrg && (
        <AddBillingOrgModal
          billingOrgs={editedUser.billing_org_ids || user.billing_org_ids || []}
          onClose={() => setShowAddBillingOrg(false)}
          onSubmit={handleAddBillingOrg}
          loading={false}
        />
      )}
    </LoadingRenderer>
  );
}

const mapDispatchToProps = dispatch => ({
  getUserDetails: userId => dispatch(actions.getUserDetails(userId)),
  editUser: (data) => dispatch(userActions.edit(data)),
  getUserFormBundle: () => dispatch(actions.getUserFormBundle()),
  sendPasswordReset: (userId, method) => dispatch(actions.sendPasswordReset(userId, method)),
  setUserPassword: (userId, password) => dispatch(actions.setUserPassword(userId, password)),
  addUserRole: (userId, roleUuid) => dispatch(actions.addUserRole(userId, roleUuid)),
  addUserNativeRole: (userId, roleUuid) => dispatch(actions.addUserNativeRole(userId, roleUuid)),
  onNavigate: path => dispatch(push(path)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  showNotification: (message, timeout, isError) => dispatch(notificationActions.show(message, timeout, isError)),
});

export default connect(null, mapDispatchToProps)(UserDetail);
